import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type SVGProps } from "react";
import { createPortal } from "react-dom";

type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>;

type ItemTone = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";

const toneStyles: Record<ItemTone, { text: string; bg: string }> = {
  default: { text: "text-slate-700", bg: "bg-[var(--color-background-primary)]" },
  primary: { text: "text-[var(--color-primary-700)]", bg: "bg-[var(--color-primary-50)]" },
  secondary: { text: "text-[var(--color-secondary-700)]", bg: "bg-[var(--color-secondary-50)]" },
  info: { text: "text-[var(--color-info-dark)]", bg: "bg-[var(--color-info-light)]" },
  success: { text: "text-[var(--color-success-dark)]", bg: "bg-[var(--color-success-light)]" },
  warning: { text: "text-[var(--color-warning-dark)]", bg: "bg-[var(--color-warning-light)]" },
  error: { text: "text-[var(--color-error-dark)]", bg: "bg-[var(--color-error-light)]" },
};

export type DropdownItem = {
  key: string;
  label: string;
  action: () => boolean | Promise<boolean>;
  icon?: SvgIcon;
  disabled?: boolean;
  tone?: ItemTone;
};

type DropdownActionsProps = {
  items: DropdownItem[];
  initialKey?: string;
  withoutInitKeyLabel?: string;
  withoutInitKeyIcon?: SvgIcon;
  disabled?: boolean;
  disabledLabel?: string;
};

export function DropdownActions({ items, initialKey, withoutInitKeyLabel, withoutInitKeyIcon, disabled = false, disabledLabel = "غیرفعال است" }: DropdownActionsProps) {

  const [open, setOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  /** When true, menu opens above the button so it stays visible near bottom of viewport */
  const openAbove = rect !== null && rect.bottom + 220 > window.innerHeight;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!initialKey) return;
    if (items.some((it) => it.key === initialKey)) setSelectedKey(initialKey);
  }, [initialKey, items]);

  useEffect(() => {
    if (!disabled) return;
    if (open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return;

    const sync = () => {
      const r = buttonRef.current?.getBoundingClientRect() ?? null;
      setRect(r);
    };

    sync();

    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);

    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      const inRoot = rootRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);

      if (!inRoot && !inMenu) setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  async function handlePick(item: DropdownItem) {
    if (item.disabled) return;

    const ok = await item.action();
    if (ok) {
      setSelectedKey(item.key);
      setOpen(false);
    }
  }

  const selectedItem = useMemo(
    () => (selectedKey === null ? null : items.find((it) => it.key === selectedKey) ?? null),
    [items, selectedKey]
  );

  const SelectedIcon = selectedItem?.icon;
  const WithoutInitIcon = withoutInitKeyIcon;

  const buttonTone: ItemTone = selectedItem?.tone ?? "default";
  const buttonStyles = toneStyles[buttonTone];

  return (
    <div
      ref={rootRef}
      className="relative inline-block"
      title={disabled ? disabledLabel : undefined}
    >
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "inline-flex items-center justify-between gap-2 px-3 h-7 text-xs rounded-2xl transition-colors whitespace-nowrap",
          buttonStyles.bg,
          buttonStyles.text,
          disabled ? "cursor-not-allowed opacity-50" : "hover:brightness-[0.98]",
        ].join(" ")}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {selectedItem ? (
            SelectedIcon ? <SelectedIcon className="h-3 w-3 shrink-0" aria-hidden /> : null
          ) : WithoutInitIcon ? (
            <WithoutInitIcon className="h-3 w-3 shrink-0" aria-hidden />
          ) : null}

          <span className="whitespace-nowrap">
            {selectedItem ? selectedItem.label : withoutInitKeyLabel}
          </span>
        </span>

        <span
          className={[
            "transition-transform duration-200",
            buttonStyles.text,
            open ? "rotate-180" : ""
          ].join(" ")}
        >
          <svg className="h-2 w-2" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
            <path d="M6 9L1 4h10z"></path>
          </svg>
        </span>
      </button>

      {    open && rect
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] min-w-[160px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
            style={{
              ...(openAbove
                ? { bottom: window.innerHeight - rect.top + 8, top: "auto" }
                : { top: rect.bottom + 8 }),
              left: rect.right,
              transform: "translateX(-100%)",
            }}
          >
            {items.map((item, index) => {
              const Icon = item.icon;
              const isSelected = item.key === selectedKey;
              const tone = item.tone ?? "default";
              const styles = toneStyles[tone];

              return (
                <div key={item.key}>
                  <button
                    type="button"
                    disabled={item.disabled}
                    onClick={() => handlePick(item)}
                    className={[
                      "flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors whitespace-nowrap",
                      item.disabled ? "cursor-not-allowed opacity-50" : "",
                      isSelected
                        ? `${styles.text} ${styles.bg}`
                        : "text-slate-700 hover:bg-[var(--color-background-primary)]",
                    ].join(" ")}
                  >
                    {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}

                    <span className="flex-1 text-right">{item.label}</span>

                    <span className="w-4 flex justify-center">
                      {isSelected && (
                        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </span>
                  </button>

                  {index !== items.length - 1 && <div className="border-t border-slate-200" />}
                </div>
              );
            })}
          </div>,
          document.body
        )
      : null}
    </div>
  );
};